import React, { useState } from 'react';
import { Modal } from 'antd';
import { Icon } from '@iconify/react';
import axios from 'axios';
import { useToast } from '../ui';

/**
 * 統一的新增帳號 Modal
 * 參考 Pinterest 風格設計 - 三欄卡片式選擇
 */
function AddAccountModal({ open, onClose, onSuccess }) {
  const { toast } = useToast();
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectingPlatform, setConnectingPlatform] = useState(null);

  const handleConnectThreads = async () => {
    try {
      setIsConnecting(true);
      setConnectingPlatform('threads');

      const token = localStorage.getItem('token');
      const response = await axios.post('user/social-accounts/pre-create', {
        platform: 'threads',
        contentType: 'mixed'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const socialAccountId = response.data.accountId;

        // Use n8n webhook for OAuth callback
        const threadsOAuthUrl = 'https://threads.net/oauth/authorize' +
          '?client_id=1198067448800544' +
          '&redirect_uri=https://viealarc.app.n8n.cloud/webhook/38e45051-2282-4106-b05d-3a371109c735' +
          '&scope=threads_basic,threads_content_publish,threads_read_replies,threads_manage_insights,threads_manage_replies,threads_keyword_search' +
          '&response_type=code' +
          '&auth_type=rerequest' +
          `&state=${socialAccountId}`;

        window.location.href = threadsOAuthUrl;
      }
    } catch (error) {
      console.error('連接 Threads 帳號時發生錯誤:', error);
      toast.error('連接失敗，請稍後再試');
    } finally {
      setIsConnecting(false);
      setConnectingPlatform(null);
    }
  };

  const accountOptions = [
    {
      id: 'threads',
      title: '連接 Threads 帳號',
      description: '連接您的 Threads 帳號，開始自動化發文與數據分析',
      icon: 'simple-icons:threads',
      iconBgColor: 'bg-gray-900',
      iconColor: 'text-white',
      buttonText: '連接帳號',
      buttonStyle: 'bg-gray-900 text-white hover:bg-gray-800',
      available: true,
      onClick: handleConnectThreads
    },
    {
      id: 'instagram',
      title: '連接 Instagram 帳號',
      description: '連接 Instagram 商業帳號，管理貼文與限時動態',
      icon: 'mdi:instagram',
      iconBgColor: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400',
      iconColor: 'text-white',
      buttonText: '即將推出',
      buttonStyle: 'bg-gray-100 text-gray-400 cursor-not-allowed',
      available: false,
      onClick: null
    },
    {
      id: 'facebook',
      title: '連接 Facebook 粉專',
      description: '連接 Facebook 粉絲專頁，統一管理社群內容',
      icon: 'mdi:facebook',
      iconBgColor: 'bg-blue-600',
      iconColor: 'text-white',
      buttonText: '即將推出',
      buttonStyle: 'bg-gray-100 text-gray-400 cursor-not-allowed',
      available: false,
      onClick: null
    }
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      centered
      closable={false}
      styles={{
        body: { padding: 0 }
      }}
    >
      <div className="p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            新增帳號
          </h2>
          <p className="text-gray-500">
            新增帳號或連接現有帳號，開始使用自動化功能
          </p>
        </div>

        {/* Account Options Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {accountOptions.map((option) => (
            <div
              key={option.id}
              className={`
                relative border-2 rounded-2xl p-6 transition-all duration-200
                ${option.available
                  ? 'border-gray-200 hover:border-gray-300 hover:shadow-md cursor-pointer'
                  : 'border-gray-100 bg-gray-50/50 opacity-70'}
              `}
            >
              {/* Icon */}
              <div className="flex justify-center mb-5">
                <div className={`w-14 h-14 rounded-full ${option.iconBgColor} flex items-center justify-center`}>
                  <Icon
                    icon={option.icon}
                    className={`text-2xl ${option.iconColor}`}
                  />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-center font-semibold text-gray-900 mb-2">
                {option.title}
              </h3>

              {/* Description */}
              <p className="text-center text-sm text-gray-500 mb-5 leading-relaxed">
                {option.description}
              </p>

              {/* Button */}
              <button
                onClick={option.available ? option.onClick : undefined}
                disabled={!option.available || (isConnecting && connectingPlatform === option.id)}
                className={`
                  w-full py-2.5 px-4 rounded-lg text-sm font-medium transition-colors
                  ${option.buttonStyle}
                  ${isConnecting && connectingPlatform === option.id ? 'opacity-70' : ''}
                `}
              >
                {isConnecting && connectingPlatform === option.id ? (
                  <span className="flex items-center justify-center gap-2">
                    <Icon icon="line-md:loading-loop" className="text-lg" />
                    連接中...
                  </span>
                ) : (
                  option.buttonText
                )}
              </button>

              {/* Coming Soon Badge */}
              {!option.available && (
                <div className="absolute top-4 right-4">
                  <span className="text-xs px-2 py-1 bg-gray-200 text-gray-500 rounded-full font-medium">
                    即將推出
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer Info */}
        <div className="text-center border-t border-gray-100 pt-6">
          <p className="text-sm text-gray-500">
            <span className="font-medium text-gray-700">管理帳號</span>
            <span className="mx-2">·</span>
            您可以隨時在設定中管理或移除已連接的帳號
          </p>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
        >
          <Icon icon="mdi:close" className="text-xl text-gray-400" />
        </button>
      </div>
    </Modal>
  );
}

export default AddAccountModal;
